/**
 * Slide-to-fire.
 *
 * The player controller has had a slide since before this module existed, and the
 * weapon system has had the *price* of firing out of one for just as long — the
 * `p.state === 1` branch in `WeaponSystem._applyRecoil` multiplies recoil by 1.25
 * and carries a comment saying it is waiting for the day sliding lets you shoot.
 * What was missing was permission.
 *
 * ## Why this is worth building and a dive is not
 *
 * Because it is already fully priced by systems that exist, in both directions:
 *
 *  - **Spread.** `Ballistics.coneFor` interpolates hip spread toward `hipMoving`
 *    on `speed / 4.2`, and a slide runs at 9.4 m/s. That clamps to 1, so a slide
 *    shot always pays the *worst* hip cone the weapon has.
 *  - **Recoil.** The 1.25 multiplier above, already written.
 *  - **Aim.** `Player._updateStance` forces `ads = false` for the whole slide, so
 *    this can only ever be hip fire. There is no version of this that gives a
 *    sniper a free moving shot.
 *
 * One of those was not true when this was first wired, and the measurement is why
 * it is now. A slide used to set `player.crouching`, which handed it the crouch
 * *accuracy* discount on top of everything else: a slide shot measured 28.8 mrad
 * against 46.2 for a standing hip shot, i.e. the conversion was a straight upgrade.
 * `Player._beginSlide` no longer claims to be crouching — see the comment there.
 *
 * So the conversion is real: you trade the accuracy of a standing shot for closing
 * ground while shooting, and the cost is charged by two systems that were already
 * charging it. A dolphin dive is the opposite case — see the note in the report and
 * in `Player.js`: its only distinct payoff over a jump is the prone landing, and
 * prone does not exist as a stance, a camera height, or a hitbox set.
 *
 * ## How permission is granted
 *
 * `WeaponSystem.update` arms `sprintOutUntil` every frame the player is sprinting
 * *or sliding*, and then computes a local `ready` from it and passes it into
 * `_handleFire`. Both the write and the read happen inside one call, so there is no
 * window in which another module's update could clear the flag — writing
 * `sprintOutUntil` from here would be overwritten before it was ever read.
 *
 * `_handleFire` is therefore wrapped, and only its `ready` argument is touched, and
 * only while the player is actually sliding with a weapon whose own
 * `mobility.slideFire` is true. No logic is copied, so nothing can drift.
 *
 * The permanent fix is one condition in `WeaponSystem.update`:
 *
 *     if (p.sprinting || p.tacSprinting || (p.state === 1 && !this.def.mobility?.slideFire)) {
 *
 * and the day that lands, this module should be deleted.
 */

/** `Player`'s internal STATE.SLIDING. Not importable — Player is a feature module. */
const PLAYER_STATE_SLIDING = 1;

export class SlideFire {
  constructor(ctx, system) {
    this.ctx = ctx;
    this.system = system;
    this.wrapped = false;
    this.shotsWhileSliding = 0;
    this._install();
  }

  /**
   * Whether the weapon in hand may fire out of a slide.
   *
   * Read off the *resolved* weapon definition rather than mirrored into this
   * module's config, so it tracks attachment changes with no coordination: the
   * attachment resolver already writes `mobility.slideFire` onto the effective def,
   * and `ctx.weapons.def` is that def.
   */
  get allowed() {
    return !!this.ctx.weapons?.def?.mobility?.slideFire;
  }

  get sliding() {
    return this.ctx.player?.state === PLAYER_STATE_SLIDING;
  }

  _install() {
    const ws = this.ctx.weapons;
    if (!ws?._handleFire) {
      // `WeaponSystem` is constructed after this module. Retry, but do not poll
      // forever if the game is running without one.
      if ((this._tries = (this._tries ?? 0) + 1) > 60) return;
      setTimeout(() => this._install(), 250);
      return;
    }
    if (ws.__slideFireWrapped) return;

    const original = ws._handleFire.bind(ws);
    this._host = ws;
    this._original = ws._handleFire;
    const self = this;

    ws._handleFire = function slideAwareHandleFire(input, now, ready) {
      if (!ready && self.sliding && self.allowed) {
        self._counting = true;
        const before = this.current.mag;
        original(input, now, true);
        if (this.current.mag < before) self.shotsWhileSliding++;
        self._counting = false;
        return undefined;
      }
      return original(input, now, ready);
    };
    ws.__slideFireWrapped = true;
    this.wrapped = true;
  }

  report() {
    return {
      /** False means `mobility.slideFire` is a dead field on every weapon. */
      wired: !!this.wrapped,
      allowedOnCurrentWeapon: this.allowed,
      sliding: this.sliding,
      shotsWhileSliding: this.shotsWhileSliding,
    };
  }

  dispose() {
    if (this._host && this._original) {
      this._host._handleFire = this._original;
      this._host.__slideFireWrapped = false;
    }
  }
}
